import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./Dashboard.css";
import DijonCard from "./DijonCard";
import axiosInstance from "../../lib/auth/axiosApi";

// Tiles come from our own cache rather than NOAA directly. NOAA renders each
// tile on demand (slow, and repeated for every viewer), and its default dpi
// hides soundings behind SCAMIN until you are very far in. The backend picks
// the service and dpi per zoom and keeps the result on disk.
// Must match RENDER_VERSION in api/views/chart_views.py. It is in the URL so
// that changing how tiles are rendered produces URLs no cache has seen -- the
// tiles carry a 30-day max-age, and after the 256px -> 512px switch nginx kept
// serving the old ones from the unchanged path.
const TILE_RENDER_VERSION = 3;
const TILE_URL =
  (axiosInstance.defaults.baseURL || "") +
  `chart-tile/v${TILE_RENDER_VERSION}/{z}/{x}/{y}.png`;

// How much we trust a row, and what color says so. The whole point of the
// card is that an unverified import and a dock we tied up to last week do not
// get to look the same.
const CONFIDENCE = {
  fresh: { color: "#2e7d32", label: "Verified < 3 months" },
  aging: { color: "#f9a825", label: "Verified this year" },
  stale: { color: "#e64a19", label: "Over a year old" },
  unverified: { color: "#757575", label: "Imported, never checked" },
};

const makeIcon = (color, hasFuel) =>
  L.divIcon({
    className: "marina-pin",
    html: `<span style="
      display:flex;align-items:center;justify-content:center;
      width:18px;height:18px;
      background:${color};
      border:2px solid #fff;
      border-radius:50%;
      box-shadow:0 0 3px rgba(0,0,0,0.5);
      font-size:10px;line-height:1;
    ">${hasFuel ? "⛽" : ""}</span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
  });

const iconCache = {};
const iconFor = (confidence, hasFuel) => {
  const key = `${confidence}-${hasFuel}`;
  if (!iconCache[key]) {
    const color = (CONFIDENCE[confidence] || CONFIDENCE.unverified).color;
    iconCache[key] = makeIcon(color, hasFuel);
  }
  return iconCache[key];
};

/** Click the chart to add a marina OSM doesn't have.
 *
 * Lighthouse Marina isn't in OpenStreetMap at all — every "Lighthouse" feature
 * in the bay is an actual lighthouse, a road or a church — so no amount of
 * query tuning finds it. Some places you just have to put on the map yourself.
 */
function AddMarinaOnClick({ enabled, onAdd }) {
  useMapEvents({
    click(event) {
      if (!enabled) return;
      const name = window.prompt("Marina name?", "");
      if (!name) return;
      const note = window.prompt(
        "Anything you know about it? (who told you, rumoured cheap fuel, etc.)",
        ""
      );
      onAdd(name.trim(), event.latlng.lat, event.latlng.lng, note || "");
    },
  });
  return null;
}

function DijonBoat(props) {
  const [marinas, setMarinas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fuelOnly, setFuelOnly] = useState(false);
  const [pumpoutOnly, setPumpoutOnly] = useState(false);
  const [addMode, setAddMode] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get("marinas/")
      .then((res) => {
        // DRF may or may not be paginating this endpoint.
        setMarinas(res.data.results || res.data || []);
        setError(null);
      })
      .catch(() => setError("Could not load marinas."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  // Recording what we know is the whole point: the map is only as good as
  // what we tell it after actually tying up somewhere.
  const setService = (marina, field) => {
    const next = marina[field] ? null : true;   // true -> unknown -> true
    axiosInstance
      .patch(`marinas/${marina.id}/`, { [field]: next })
      .then((res) =>
        setMarinas((rows) => rows.map((r) => (r.id === res.data.id ? res.data : r)))
      )
      .catch(() => setError("Could not save that."));
  };

  const logFuel = (marina, fuelType) => {
    const raw = window.prompt(`Price per gallon for ${fuelType}?`, "");
    if (raw === null) return;
    const price = parseFloat(raw);
    if (isNaN(price)) return;
    axiosInstance
      .post("fuel-reports/", {
        marina: marina.id,
        fuel_type: fuelType,
        price_per_gallon: price.toFixed(3),
        date: new Date().toISOString().slice(0, 10),
      })
      .then(load)
      .catch(() => setError("Could not save that."));
  };

  const addMarina = (name, lat, lon, note) => {
    axiosInstance
      .post("marinas/", {
        name,
        latitude: lat,
        longitude: lon,
        source: "manual",
        // Deliberately NOT verified. Dropping a pin because someone mentioned
        // a place is not the same as having been there, and marking it "fresh"
        // would make hearsay look like first-hand knowledge. Use the popup's
        // confirm button once you've actually pulled in.
        notes: note || "",
      })
      .then(() => {
        setAddMode(false);
        load();
      })
      .catch(() => setError("Could not add that."));
  };

  const verify = (marina) => {
    axiosInstance
      .post(`marinas/${marina.id}/verify/`, { note: "Confirmed from the boat card" })
      .then((res) =>
        setMarinas((rows) => rows.map((r) => (r.id === res.data.id ? res.data : r)))
      )
      .catch(() => setError("Could not save that."));
  };

  const visible = useMemo(
    () =>
      marinas.filter(
        (m) =>
          (!fuelOnly || m.has_fuel) &&
          (!pumpoutOnly || m.has_pumpout) &&
          m.latitude != null &&
          m.longitude != null
      ),
    [marinas, fuelOnly, pumpoutOnly]
  );

  const counts = useMemo(() => {
    const out = { fresh: 0, aging: 0, stale: 0, unverified: 0 };
    marinas.forEach((m) => {
      out[m.confidence] = (out[m.confidence] || 0) + 1;
    });
    return out;
  }, [marinas]);

  const services = (m) =>
    [
      m.has_gas && "gas",
      m.has_diesel && "diesel",
      m.has_pumpout && "pumpout",
      m.has_transient_slips && "transient",
    ].filter(Boolean);

  return (
    <div>
      <DijonCard
        width={"720px"}
        height={"640px"}
        startMaximized={true}
        activeCards={props.activeCards}
        setActiveCards={props.setActiveCards}
        name={"boat"}
        content={
          <div style={{ height: "100%", width: "100%", position: "relative" }}>
            <MapContainer
              center={[41.63, -71.35]}
              zoom={12}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%", background: "#cfe3f2" }}
            >
              <AddMarinaOnClick enabled={addMode} onAdd={addMarina} />
              <TileLayer
                url={TILE_URL}
                // tileSize stays at Leaflet's default 256. The backend renders
                // each tile at 512 px for the same 256-grid bbox, which is the
                // standard hidpi trick: two device pixels per CSS pixel, so the
                // depth figures are sharp on a 2x display instead of upscaled
                // mush. Setting tileSize={512} here instead broke the map
                // outright -- Leaflet divides its 256-based world bounds by
                // tileSize, so it asked for half the index range and every
                // request landed in open ocean.
                minZoom={8}
                maxZoom={17}
                attribution="Chart: NOAA ENC &mdash; not for navigation"
              />

              {visible.map((m) => (
                <Marker
                  key={m.id}
                  position={[m.latitude, m.longitude]}
                  icon={iconFor(m.confidence, m.has_fuel)}
                >
                  <Popup>
                    <div style={{ minWidth: "190px" }}>
                      <div style={{ fontWeight: 700, marginBottom: "3px" }}>{m.name}</div>

                      <div style={{ fontSize: "11px", marginBottom: "5px" }}>
                        {services(m).length
                          ? services(m).join(" · ")
                          : "no services recorded"}
                      </div>

                      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap",
                                    marginBottom: "5px" }}>
                        {[
                          ["has_gas", "gas"],
                          ["has_diesel", "diesel"],
                          ["has_pumpout", "pumpout"],
                          ["has_transient_slips", "transient"],
                        ].map(([field, label]) => (
                          <span
                            key={field}
                            onClick={() => setService(m, field)}
                            title="Click to record what you saw"
                            style={{
                              fontSize: "10px",
                              padding: "2px 7px",
                              borderRadius: "10px",
                              cursor: "pointer",
                              userSelect: "none",
                              border: "1px solid " + (m[field] ? "#2e7d32" : "#bbb"),
                              background: m[field] ? "#2e7d32" : "transparent",
                              color: m[field] ? "#fff" : "#666",
                            }}
                          >
                            {m[field] ? "✓ " : "+ "}
                            {label}
                          </span>
                        ))}
                      </div>

                      {m.depth_at_dock != null && (
                        <div style={{ fontSize: "11px" }}>
                          {m.depth_at_dock} ft at the dock
                        </div>
                      )}
                      {m.vhf_channel && (
                        <div style={{ fontSize: "11px" }}>VHF {m.vhf_channel}</div>
                      )}
                      {m.phone && <div style={{ fontSize: "11px" }}>{m.phone}</div>}
                      {m.fuel_hours && (
                        <div style={{ fontSize: "11px" }}>Fuel: {m.fuel_hours}</div>
                      )}

                      {m.latest_fuel && (
                        <div style={{ fontSize: "11px", marginTop: "3px" }}>
                          Last paid ${m.latest_fuel.price_per_gallon}/gal (
                          {m.latest_fuel.fuel_type}) on {m.latest_fuel.date}
                        </div>
                      )}

                      <div
                        style={{
                          fontSize: "11px",
                          marginTop: "5px",
                          color: (CONFIDENCE[m.confidence] || CONFIDENCE.unverified)
                            .color,
                          fontWeight: 600,
                        }}
                      >
                        {m.last_verified
                          ? `Verified ${m.last_verified} (${m.staleness_days}d ago)`
                          : "Never verified — treat as a lead, not a fact"}
                      </div>

                      {m.notes && (
                        <div style={{ fontSize: "11px", marginTop: "4px",
                                      fontStyle: "italic", color: "#555" }}>
                          {m.notes}
                        </div>
                      )}

                      {m.coast_pilot_text && (
                        <details style={{ fontSize: "10px", marginTop: "4px" }}>
                          <summary style={{ cursor: "pointer" }}>Coast Pilot</summary>
                          <div style={{ color: "#555", marginTop: "3px" }}>
                            {m.coast_pilot_text.slice(0, 400)}
                          </div>
                        </details>
                      )}

                      <div style={{ display: "flex", gap: "4px", marginTop: "6px",
                                    flexWrap: "wrap" }}>
                        <button
                          onClick={() => verify(m)}
                          style={{ fontSize: "11px", padding: "3px 8px", cursor: "pointer" }}
                        >
                          I confirmed this today
                        </button>
                        {/* Logging a price is also first-hand proof the dock pumps,
                            so the backend marks the marina verified from it. */}
                        <button
                          onClick={() => logFuel(m, "gas")}
                          style={{ fontSize: "11px", padding: "3px 8px", cursor: "pointer" }}
                        >
                          ⛽ logged gas
                        </button>
                        <button
                          onClick={() => logFuel(m, "diesel")}
                          style={{ fontSize: "11px", padding: "3px 8px", cursor: "pointer" }}
                        >
                          ⛽ logged diesel
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            {/* Filters + legend */}
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                zIndex: 1000,
                background: "rgba(255,255,255,0.92)",
                padding: "8px 10px",
                borderRadius: "6px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                fontSize: "12px",
              }}
            >
              <div
                onClick={() => setFuelOnly((v) => !v)}
                style={{ cursor: "pointer", userSelect: "none", marginBottom: "3px" }}
              >
                {fuelOnly ? "☑" : "☐"} ⛽ Fuel only
              </div>
              <div
                onClick={() => setPumpoutOnly((v) => !v)}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                {pumpoutOnly ? "☑" : "☐"} Pumpout only
              </div>

              <div
                onClick={() => setAddMode((v) => !v)}
                title="Then click the chart where it is"
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  marginTop: "5px",
                  fontWeight: addMode ? 700 : 400,
                  color: addMode ? "#2e7d32" : "inherit",
                }}
              >
                {addMode ? "● click the chart…" : "＋ add a marina"}
              </div>

              <hr style={{ margin: "6px 0", borderColor: "#ddd" }} />
              <div style={{ fontWeight: 700, marginBottom: "4px" }}>How current?</div>
              {Object.entries(CONFIDENCE).map(([key, meta]) => (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "3px",
                  }}
                  title={meta.label}
                >
                  <span
                    style={{
                      width: "11px",
                      height: "11px",
                      borderRadius: "50%",
                      background: meta.color,
                      border: "1px solid #fff",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: "11px" }}>
                    {meta.label} ({counts[key] || 0})
                  </span>
                </div>
              ))}
            </div>

            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "10px",
                zIndex: 1000,
                fontSize: "10px",
                color: "#666",
                background: "rgba(255,255,255,0.85)",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              {loading
                ? "Loading marinas…"
                : error
                ? error
                : `${visible.length} shown · chart: NOAA ENC · not for navigation`}
            </div>
          </div>
        }
      />
    </div>
  );
}

export default DijonBoat;
